// Manual posting cancellation. Shares the backend classic-script bindings.
const assertManualPostingAnnulAllowed = (existing, options = {}) => {
  assertSimpleVatMutationAllowed({
    previousDate: existing?.date,
    nextDate: options.annulEffectiveDate || existing?.date,
    vatAffects: true,
    vatPeriodAffects: !window.PlummeManualPostingRules.isWithoutVat(
      existing,
      currentCountry(),
    ),
  });
  assertBankOpenForPayments(
    runtime.appState,
    window.PlummeManualPostingRules.bankPayments(existing),
  );
};
const annulManualPostingLocal = async (postingId, options = {}) => {
  const existing = safeArray(runtime.appState?.expenses).find(
    (expense) => expense.id === postingId && expense.manualPosting,
  );
  assertManualPostingAnnulAllowed(existing, options);
  const now = nowIso();
  const comment = String(options.annulComment || options.comment || "").trim();
  const linkedReceiptId = String(existing?.receiptInboxEntryId || "").trim();
  const linkedOpenBankLine = safeArray(runtime.appState?.bankStatements).some(
    (statement) =>
      !statement?.bookedAt &&
      !statement?.reconciledAt &&
      !["archived", "reconciled", "booked"].includes(
        String(statement?.status || "").trim(),
      ) &&
      safeArray(statement?.lines).some(
        (line) => String(line?.matchedEntryId || "").trim() === postingId,
      ),
  );
  const reversalDate = options.annulEffectiveDate || (linkedOpenBankLine
    ? String(existing?.date || "").slice(0, 10)
    : now.slice(0, 10));
  const reopenedLineNote =
    comment || "Den frie posteringen er reversert. Kontroller banklinjen på nytt.";
  let nextPosting = null;
  await updateStateAndPersist((state) => ({
    ...state,
    expenses: safeArray(state.expenses).map((expense) => {
      if (expense.id !== postingId || !expense.manualPosting) return expense;
      nextPosting = stripUndefinedDeep({
        ...expense,
        status: "annulled",
        annulledAt: expense.annulledAt || now,
        reversalDate: expense.reversalDate || reversalDate,
        annulComment: comment || expense.annulComment,
        updatedAt: now,
      });
      return nextPosting;
    }),
    receiptInbox: linkedReceiptId
      ? safeArray(state.receiptInbox).map((entry) =>
          entry.id === linkedReceiptId
            ? stripUndefinedDeep({
                ...entry,
                status: "ready",
                bookedAt: undefined,
                bookedAs: undefined,
                linkedEntryId: undefined,
              })
            : entry,
        )
      : safeArray(state.receiptInbox),
    bankStatements: safeArray(state.bankStatements).map((statement) => ({
      ...statement,
      lines: safeArray(statement.lines).map((line) => {
        if (String(line?.matchedEntryId || "").trim() !== postingId) return line;
        const nextLine = { ...line };
        delete nextLine.matchedEntryId;
        delete nextLine.matchedEntryType;
        delete nextLine.bankTrace;
        return stripUndefinedDeep({
          ...nextLine,
          reconciliationStatus: "manual-review",
          note: reopenedLineNote,
        });
      }),
    })),
  }));
  if (!nextPosting) throw new Error(t("svc.manualEntryToCancelNotFound"));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return nextPosting;
};
const annulManualPosting = async (postingOrId, options = {}) => {
  const postingId =
    typeof postingOrId === "string" ? postingOrId : postingOrId?.id;
  if (!postingId) throw new Error(t("svc.missingManualEntryCancel"));
  const existing = safeArray(runtime.appState?.expenses).find(
    (expense) => expense.id === postingId && expense.manualPosting,
  );
  assertManualPostingAnnulAllowed(existing, options);
  try {
    const result = await authorizedPost("manualPostingMutationHttp", {
      businessId: runtime.activeBusinessId,
      action: "annul",
      postingId,
      annulEffectiveDate: options.annulEffectiveDate,
      annulComment: options.annulComment || options.comment || "",
    });
    if (result.state) {
      runtime.appState = mergeAppMutationState(result.state);
      emitRuntime();
    }
    window.dispatchEvent(new Event(BACKEND_EVENTS.state));
    return safeArray(result.postings)[0] || null;
  } catch (error) {
    if (!shouldFallbackToClientStateWrite(error)) throw error;
    return annulManualPostingLocal(postingId, options);
  }
};

// Loaded by backend.jsx in the shared classic-script scope.
